import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { 
  MapPin, Clock, Users, Eye, TrendingUp, 
  Calendar, DollarSign, ShoppingCart, UserCheck,
  Activity, BarChart3, PieChart
} from 'lucide-react';
import LoadingSpinner from '../../../components/LoadingSpinner';

interface AnalyticsData {
  totalOrders: number;
  totalRevenue: number;
  totalUsers: number;
  completedProfiles: number;
  ordersByRegion: { [key: string]: number };
  ordersByTime: { [key: string]: number };
  ordersByDay: { [key: string]: number };
  recentOrders: any[];
  topServices: { name: string; count: number }[];
}

export default function AnalyticsView() {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalOrders: 0,
    totalRevenue: 0,
    totalUsers: 0,
    completedProfiles: 0,
    ordersByRegion: {},
    ordersByTime: {},
    ordersByDay: {},
    recentOrders: [],
    topServices: []
  });
  const [websiteVisits, setWebsiteVisits] = useState(0);

  useEffect(() => {
    loadAnalytics();
    loadWebsiteVisits();
  }, []);

  const loadWebsiteVisits = () => {
    // Get website visits from localStorage
    const visits = localStorage.getItem('websiteVisits');
    if (visits) {
      setWebsiteVisits(parseInt(visits));
    }
  };

  const loadAnalytics = async () => {
    try {
      setLoading(true);

      // Fetch Orders
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      const orders = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as any[];

      // Fetch Users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as any[];

      // Calculate metrics
      const totalOrders = orders.length;
      const totalRevenue = orders.reduce((sum, order) => sum + (order.totalAmount || 0), 0);
      const totalUsers = users.length;

      // Count completed profiles (users with phone, address, etc.)
      const completedProfiles = users.filter(user => 
        user.displayName && 
        user.email && 
        user.phoneNumber && 
        user.address
      ).length;

      // Orders by Region (Himachal Pradesh Cities)
      const ordersByRegion: { [key: string]: number } = {};
      orders.forEach(order => {
        // Try to get city from address object first, then location, then state
        const city = order.address?.city || order.city || order.location || 'Unknown City';
        ordersByRegion[city] = (ordersByRegion[city] || 0) + 1;
      });

      // Orders by Time (Hour of day)
      const ordersByTime: { [key: string]: number } = {};
      orders.forEach(order => {
        if (order.createdAt) {
          const date = new Date(order.createdAt);
          const hour = date.getHours();
          const timeSlot = `${hour}:00-${hour + 1}:00`;
          ordersByTime[timeSlot] = (ordersByTime[timeSlot] || 0) + 1;
        }
      });

      // Orders by Day of Week
      const ordersByDay: { [key: string]: number } = {
        'Monday': 0,
        'Tuesday': 0,
        'Wednesday': 0,
        'Thursday': 0,
        'Friday': 0,
        'Saturday': 0,
        'Sunday': 0
      };
      orders.forEach(order => {
        if (order.createdAt) {
          const date = new Date(order.createdAt);
          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const day = dayNames[date.getDay()];
          ordersByDay[day]++;
        }
      });

      // Top Services
      const serviceCount: { [key: string]: number } = {};
      orders.forEach(order => {
        order.items?.forEach((item: any) => {
          const serviceName = item.serviceName || item.name;
          serviceCount[serviceName] = (serviceCount[serviceName] || 0) + 1;
        });
      });
      const topServices = Object.entries(serviceCount)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      setAnalytics({
        totalOrders,
        totalRevenue,
        totalUsers,
        completedProfiles,
        ordersByRegion,
        ordersByTime,
        ordersByDay,
        recentOrders: orders.slice(0, 5),
        topServices
      });

    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[600px]">
        <LoadingSpinner />
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Revenue',
      value: `₹${analytics.totalRevenue.toLocaleString()}`,
      icon: DollarSign,
      color: 'bg-green-500',
      bgColor: 'bg-green-100',
      textColor: 'text-green-700'
    },
    {
      title: 'Total Orders',
      value: analytics.totalOrders.toString(),
      icon: ShoppingCart,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-700'
    },
    {
      title: 'Total Users',
      value: analytics.totalUsers.toString(),
      icon: Users,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-700'
    },
    {
      title: 'Completed Profiles',
      value: `${analytics.completedProfiles}/${analytics.totalUsers}`,
      icon: UserCheck,
      color: 'bg-orange-500',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-700'
    },
    {
      title: 'Website Visits',
      value: websiteVisits.toLocaleString(),
      icon: Eye,
      color: 'bg-pink-500',
      bgColor: 'bg-pink-100',
      textColor: 'text-pink-700'
    },
    {
      title: 'Profile Completion',
      value: `${((analytics.completedProfiles / analytics.totalUsers) * 100 || 0).toFixed(1)}%`,
      icon: Activity,
      color: 'bg-teal-500',
      bgColor: 'bg-teal-100',
      textColor: 'text-teal-700'
    }
  ];

  // Get top 5 regions
  const topRegions = Object.entries(analytics.ordersByRegion)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  // Get peak hours (top 5 time slots)
  const peakHours = Object.entries(analytics.ordersByTime)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  // Get busiest days
  const busiestDays = Object.entries(analytics.ordersByDay)
    .sort(([, a], [, b]) => b - a);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
        <p className="text-gray-600 mt-1">View platform analytics and insights</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
              </div>
              <div className={`${stat.bgColor} p-3 rounded-lg`}>
                <stat.icon className={`h-6 w-6 ${stat.textColor}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Orders by City */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <MapPin className="h-5 w-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Top Cities (Himachal Pradesh)</h3>
          </div>
          <div className="space-y-4">
            {topRegions.length > 0 ? (
              topRegions.map(([region, count], index) => {
                const percentage = (count / analytics.totalOrders) * 100;
                return (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">{region}</span>
                      <span className="text-sm text-gray-600">{count} orders ({percentage.toFixed(1)}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-primary-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-8">No order data available</p>
            )}
          </div>
        </div>

        {/* Peak Hours */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Clock className="h-5 w-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Peak Hours</h3>
          </div>
          <div className="space-y-4">
            {peakHours.length > 0 ? (
              peakHours.map(([time, count], index) => {
                const maxCount = Math.max(...Object.values(analytics.ordersByTime));
                const percentage = (count / maxCount) * 100;
                return (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">{time}</span>
                      <span className="text-sm text-gray-600">{count} orders</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-8">No order data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Additional Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Orders by Day of Week */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Calendar className="h-5 w-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Orders by Day</h3>
          </div>
          <div className="space-y-3">
            {busiestDays.map(([day, count], index) => {
              const maxCount = Math.max(...Object.values(analytics.ordersByDay));
              const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
              return (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 w-24">{day}</span>
                    <div className="flex-1 mx-4">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-sm text-gray-600 w-16 text-right">{count} orders</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Services */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="h-5 w-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Top Services</h3>
          </div>
          <div className="space-y-4">
            {analytics.topServices.length > 0 ? (
              analytics.topServices.map((service, index) => {
                const maxCount = analytics.topServices[0].count;
                const percentage = (service.count / maxCount) * 100;
                return (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700 truncate flex-1">{service.name}</span>
                      <span className="text-sm text-gray-600 ml-4">{service.count} orders</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-8">No service data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Key Insights */}
      <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-xl border border-primary-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="h-5 w-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Key Insights</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4">
            <p className="text-sm text-gray-600">Average Order Value</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              ₹{analytics.totalOrders > 0 ? Math.round(analytics.totalRevenue / analytics.totalOrders).toLocaleString() : 0}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <p className="text-sm text-gray-600">Top City (HP)</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {topRegions.length > 0 ? topRegions[0][0] : 'N/A'}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <p className="text-sm text-gray-600">Busiest Day</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {busiestDays.length > 0 ? busiestDays[0][0] : 'N/A'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
